import { environment } from '../../../environments/environment';

export interface ConsentPurpose {
  key: string;
  label: string;
  description: string;
  purposeIds: number[];
}

export interface ConsentSnapshot {
  regionResolved: boolean;
  inScope: boolean;
  region: string | null;
  purposes: ConsentPurpose[];
  choices: Record<string, boolean> | null;
  recorded: boolean;
}

export interface StoredConsent {
  version: 1;
  region: string | null;
  choices: Record<string, boolean>;
  tcString: string;
  gppString: string;
  usPrivacy: string;
  updatedAt: string;
}

export interface AdRequestConsentParams {
  gdpr?: string;
  gdpr_consent?: string;
  us_privacy?: string;
  gpp?: string;
  gpp_sid?: string;
}

/** EU-27 plus Iceland, Liechtenstein, and Norway. UK/GB is not EEA and is out of SRS-50 scope. */
export const EEA_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO',
] as const;

export const EEA_COUNTRIES = new Set<string>(EEA_COUNTRY_CODES);

export const STORAGE_KEY = 'dxk.privacy.consent';
export const REGION_OVERRIDE_KEY = 'dxk.consent.regionOverride';
export const TCF_COOKIE = 'euconsent-v2';
export const GPP_COOKIE = 'gpp';
export const US_PRIVACY_COOKIE = 'usprivacy';

/** CCPA string used when the banner is EU/EEA and US privacy does not apply. */
export const US_PRIVACY_NOT_APPLICABLE = '1---';

/** GPP section id 2 is TCF EU. Do not also emit US National (7) from this banner. */
export const TCF_EU_GPP_SID = '2';

export const TCF_PURPOSE_GROUPS: ConsentPurpose[] = [
  {
    key: 'storage',
    label: 'Store and access information',
    description: 'Save consent and related information on this device (TCF purpose 1).',
    purposeIds: [1],
  },
  {
    key: 'ads',
    label: 'Advertising',
    description: 'Select basic and personalised ads (TCF purposes 2, 3, and 4).',
    purposeIds: [2, 3, 4],
  },
  {
    key: 'measurement',
    label: 'Measurement',
    description: 'Measure ad and content performance and build audience insights (TCF purposes 7, 8, and 9).',
    purposeIds: [7, 8, 9],
  },
  {
    key: 'content',
    label: 'Personalised content',
    description: 'Build a content profile and select content, including limited data selection (TCF purposes 5, 6, and 11).',
    purposeIds: [5, 6, 11],
  },
  {
    key: 'improve',
    label: 'Product improvement',
    description: 'Use data to develop and improve products (TCF purpose 10).',
    purposeIds: [10],
  },
];

export function emptyConsentSnapshot(): ConsentSnapshot {
  return {
    regionResolved: false,
    inScope: false,
    region: null,
    purposes: TCF_PURPOSE_GROUPS,
    choices: null,
    recorded: false,
  };
}

export function purposeIdsFromChoices(choices: Record<string, boolean> | null): number[] {
  if (!choices) {
    return [];
  }
  const ids = new Set<number>();
  TCF_PURPOSE_GROUPS.forEach((purpose) => {
    if (choices[purpose.key]) {
      purpose.purposeIds.forEach((id) => ids.add(id));
    }
  });
  return [...ids].sort((a, b) => a - b);
}

export function isExchangeUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    const vastHost = new URL(environment.vastUrl).hostname.toLowerCase();
    return host === vastHost || host === 'x.videobyte.com' || host === 'dev-x.videobyte.com';
  } catch {
    return false;
  }
}

export function appendConsentQuery(url: string, params: AdRequestConsentParams): string {
  if (!url || !isExchangeUrl(url)) {
    return url;
  }
  try {
    const parsed = new URL(url);
    Object.keys(params).forEach((key) => {
      const value = params[key as keyof AdRequestConsentParams];
      if (!value || parsed.searchParams.has(key)) {
        return;
      }
      parsed.searchParams.set(key, value);
    });
    return parsed.toString();
  } catch {
    return url;
  }
}
